import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import {
  Information,
  InformationListResponse,
  InformationResponse,
  SingleInformationResponse,
} from '@/types/information';

// ==================== API Service Functions ====================

/**
 * Get all announcements (current + past)
 */
const getAllInformations = async (
  site: string,
  clinicNumber: string
): Promise<{
  current: Information[];
  past: Information[];
}> => {
  const response = await apiClient.get<InformationListResponse>(clinicApiPath(site, clinicNumber, '/informations'));
  return response.data.data;
};

/**
 * Get current announcements only
 */
const getCurrentInformations = async (site: string, clinicNumber: string): Promise<Information[]> => {
  const response = await apiClient.get<InformationResponse>(clinicApiPath(site, clinicNumber, '/informations/current'));
  return response.data.data;
};

/**
 * Get past announcements only
 */
const getPastInformations = async (
  site: string,
  clinicNumber: string,
  months?: number
): Promise<Information[]> => {
  const params = months ? { months } : {};
  const response = await apiClient.get<InformationResponse>(clinicApiPath(site, clinicNumber, '/informations/past'), { params });
  return response.data.data;
};

/**
 * Get a single announcement by ID
 */
const getInformation = async (site: string, clinicNumber: string, id: number): Promise<Information> => {
  const response = await apiClient.get<SingleInformationResponse>(clinicApiPath(site, clinicNumber, `/informations/${id}`));
  return response.data.data;
};

// ==================== Hooks ====================

/**
 * Hook to get all informations (current + past)
 */
export const useMainAllInformations = (site: string, clinicNumber: string) => {
  return useQuery({
    queryKey: ['informations', 'all', site, clinicNumber],
    queryFn: () => getAllInformations(site, clinicNumber),
    enabled: !!site && !!clinicNumber,
  });
};

/**
 * Hook to get current informations only
 */
export const useMainCurrentInformations = (site: string, clinicNumber: string) => {
  return useQuery({
    queryKey: ['informations', 'current', site, clinicNumber],
    queryFn: () => getCurrentInformations(site, clinicNumber),
    enabled: !!site && !!clinicNumber,
  });
};

/**
 * Hook to get past informations
 */
export const useMainPastInformations = (site: string, clinicNumber: string, months?: number) => {
  return useQuery({
    queryKey: ['informations', 'past', site, clinicNumber, months],
    queryFn: () => getPastInformations(site, clinicNumber, months),
    enabled: !!site && !!clinicNumber,
  });
};

/**
 * Hook to get a single information by ID
 */
export const useMainInformation = (site: string, clinicNumber: string, id: number) => {
  return useQuery({
    queryKey: ['informations', 'detail', site, clinicNumber, id],
    queryFn: () => getInformation(site, clinicNumber, id),
    enabled: !!site && !!clinicNumber && !!id,
  });
};
